import React, { useCallback, useState, FC, useMemo } from "react";
import { LayoutChangeEvent } from "react-native";
import { View, ALIGN, Link } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Text } from "@lookiero/sty-psp-ui";
import { Feedback, ProductWithFeedback } from "../../../../../../domain/entities/order/Order";
import { Question, QuestionType } from "../../../../../../domain/entities/question/Question";
import { ProductItem } from "../../../../components/productItem/ProductItem";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { FeedbackComment } from "./FeedbackComment";
import { style as feedbackListStyle } from "./FeedbackList.style";
import { FeedbackSelectQuestion } from "./FeedbackSelectQuestion";
import { InfoBanner } from "./infoBanner/InfoBanner";

interface FeedbackListProps {
  readonly questions?: Record<string, Question[]>;
  readonly selectedItems: ProductWithFeedback[]; // TODO: duplicate responsiblity! (it's the same as products)
  readonly onNext: () => void;
  readonly onBack?: () => void;
  readonly onLayout?: (event: LayoutChangeEvent, productId: string) => void;
  readonly onError?: (productId: string) => void;
  readonly onQuestionsUnavailable?: () => void;
}

const FeedbackList: FC<FeedbackListProps> = ({
  questions,
  selectedItems,
  onBack,
  onLayout = () => void 0,
  onError,
  onQuestionsUnavailable,
  onNext,
}) => {
  const canReturnProducts = !selectedItems || selectedItems.length === 0;
  const [isFeedbackEmptyPerItem, setIsFeedbackEmptyPerItem] = useState<Record<string, boolean>>({});
  const isFeedbackEmpty = (item: ProductWithFeedback) => {
    return (!item.feedback?.comment || item.feedback?.comment.content === "") && !item.feedback?.answers;
  };

  const emptyOptionText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.EMPTY_OPTION,
  });
  const returnFeedbackOpinionText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURN_FEEDBACK_OPINION,
  });
  const returnFeedbackWhyText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURN_FEEDBACK_WHY,
  });
  const confirmReturnFeedbackEmptyErrorText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CONFIRM_RETURN_FEEDBACK_EMPTY_ERROR,
  });
  const returnFeedbackContinueText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURN_FEEDBACK_CONTINUE,
  });
  const returnFeedbackMoreProductsText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RETURN_FEEDBACK_MORE_PRODUCTS,
  });

  const style = useMemo(() => feedbackListStyle(), []);

  const onChangeInputField = useCallback(
    (questionId: string, answerId: string, product: ProductWithFeedback) => {
      if (!product.feedback) {
        product.feedback = {} as Feedback;
      }
      const feedback = product.feedback;
      feedback.answers = feedback.answers ?? [];
      feedback.answers = feedback.answers.filter((answer) => answer.questionId !== questionId);
      if (answerId !== emptyOptionText) {
        feedback.answers.push({
          questionId,
          answerId,
        });
        setIsFeedbackEmptyPerItem((prevState) => ({ ...prevState, [product.itemId]: answerId === emptyOptionText }));
      }
    },
    [emptyOptionText],
  );

  const onChangeTextAreaField = useCallback((questionId: string, content: string, product: ProductWithFeedback) => {
    if (!product.feedback) {
      product.feedback = {
        answers: [],
        comment: undefined,
      };
    }
    const feedback = product.feedback;
    feedback.comment = {
      questionId,
      content,
    };
    setIsFeedbackEmptyPerItem((prevState) => ({ ...prevState, [product.itemId]: false }));
  }, []);

  const handlePressRequestReturn = useCallback(async () => {
    if (canReturnProducts) {
      return;
    }

    if (selectedItems.some((item) => !questions?.[item._variantId])) {
      onQuestionsUnavailable?.();
      return;
    }

    let shouldNotSend = false;
    selectedItems?.forEach((item) => {
      if (isFeedbackEmpty(item)) {
        shouldNotSend = true;
        setIsFeedbackEmptyPerItem((prevState) => ({ ...prevState, [item.itemId]: true }));
      }
    });

    if (shouldNotSend) {
      if (onError) {
        const firstItemWithError = selectedItems.find((item) => isFeedbackEmpty(item));
        onError(firstItemWithError?.itemId || "");
      }
      return;
    }

    onNext();
  }, [canReturnProducts, selectedItems, questions, onNext, onError, onQuestionsUnavailable]);

  return (
    <View>
      {selectedItems.map((product) => {
        const productQuestions = questions ? questions[product._variantId] : [];

        return (
          <View
            key={product.itemId}
            style={style.productQuestions}
            onLayout={(event) => onLayout(event, product.itemId)}
          >
            <View style={style.productWrapper}>
              <ProductItem product={product} />
            </View>
            {productQuestions ? (
              <>
                <Text level={3} style={style.questionTitle}>
                  {returnFeedbackOpinionText}
                </Text>
                <Text level={3} style={style.questionSubtitle}>
                  {returnFeedbackWhyText}
                </Text>
                {isFeedbackEmptyPerItem[product.itemId] && (
                  <InfoBanner style={style.errorBanner}>
                    <Text level={2} variant="detail">
                      {confirmReturnFeedbackEmptyErrorText}
                    </Text>
                  </InfoBanner>
                )}
                {productQuestions.map((question: Question) => {
                  const isVisibleField =
                    question.type === QuestionType.GIVEN_ANSWER || question.type === QuestionType.COMMENT;

                  if (!isVisibleField) {
                    return null;
                  }

                  if (question.type === QuestionType.GIVEN_ANSWER) {
                    return (
                      <FeedbackSelectQuestion
                        key={question.id}
                        answers={product.feedback?.answers}
                        question={question}
                        onChangeField={(questionId, answerId) => onChangeInputField(questionId, answerId, product)}
                      />
                    );
                  }

                  return (
                    <FeedbackComment
                      key={question.id}
                      comment={product.feedback?.comment}
                      question={question}
                      onChangeField={(questionId, content) => onChangeTextAreaField(questionId, content, product)}
                    />
                  );
                })}
              </>
            ) : undefined}
          </View>
        );
      })}
      <View style={style.actions}>
        <Button testID="feedback-button" onPress={handlePressRequestReturn}>
          {returnFeedbackContinueText}
        </Button>
        <View style={style.link}>
          <Link align={ALIGN.CENTER} level={3} action underlined onPress={onBack}>
            {returnFeedbackMoreProductsText}
          </Link>
        </View>
      </View>
    </View>
  );
};

export { FeedbackList };
